import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    IconButton,
    Autocomplete,
    Button,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { zohoItemAPI } from '../../api/zohoItem';
import { salesOrdersAPI } from '../../api/salesOrders';

interface SOItem {
    id?: number;
    item_id: number;
    item_name?: string;
    quantity: number;
    unit_price: number;
    price_source?: string;
    line_total: number;
    notes?: string;
}

interface SOItemsManagerProps {
    items: SOItem[];
    onChange: (items: SOItem[]) => void;
    customerId: number;
    orderDate: string;
    editMode?: boolean;
}

const SOItemsManager: React.FC<SOItemsManagerProps> = ({
    items,
    onChange,
    customerId,
    orderDate,
    editMode = false,
}) => {
    const [localItems, setLocalItems] = useState<SOItem[]>(items);
    const [availableItems, setAvailableItems] = useState<any[]>([]);
    const [selectedItem, setSelectedItem] = useState<any>(null);

    useEffect(() => {
        loadAvailableItems();
    }, []);

    useEffect(() => {
        setLocalItems(items);
    }, [items]);

    const loadAvailableItems = async () => {
        try {
            const data = await zohoItemAPI.getItems();
            setAvailableItems(data.items || data || []);
        } catch (error) {
            console.error('Failed to load items:', error);
        }
    };

    const handleAddItem = async () => {
        if (!selectedItem) return;

        try {
            // Get price for this item
            const priceInfo = await salesOrdersAPI.getItemPrice(
                customerId,
                selectedItem.id,
                orderDate
            );

            const newItem: SOItem = {
                item_id: selectedItem.id,
                item_name: selectedItem.name,
                quantity: 1,
                unit_price: priceInfo.price || 0,
                price_source: priceInfo.source,
                line_total: priceInfo.price || 0,
            };

            const updated = [...localItems, newItem];
            setLocalItems(updated);
            onChange(updated);
            setSelectedItem(null);
        } catch (error) {
            console.error('Failed to add item:', error);
        }
    };

    const handleRemoveItem = (index: number) => {
        const updated = localItems.filter((_, i) => i !== index);
        setLocalItems(updated);
        onChange(updated);
    };

    const handleQuantityChange = (index: number, quantity: number) => {
        if (quantity <= 0) return;

        const updated = [...localItems];
        updated[index].quantity = quantity;
        updated[index].line_total = quantity * updated[index].unit_price;
        setLocalItems(updated);
        onChange(updated);
    };

    const handlePriceChange = (index: number, price: number) => {
        if (price < 0) return;

        const updated = [...localItems];
        updated[index].unit_price = price;
        updated[index].price_source = 'manual';
        updated[index].line_total = updated[index].quantity * price;
        setLocalItems(updated);
        onChange(updated);
    };

    const handleNotesChange = (index: number, notes: string) => {
        const updated = [...localItems];
        updated[index].notes = notes;
        setLocalItems(updated);
        onChange(updated);
    };

    const total = localItems.reduce((sum, item) => sum + Number(item.line_total || 0), 0);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Items
            </Typography>

            {/* Items Table */}
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Unit Price (₹)</TableCell>
                            <TableCell align="right">Total (₹)</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {localItems.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography color="text.secondary">No items added</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            localItems.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Typography variant="body2">{item.item_name || `Item ${item.item_id}`}</Typography>
                                        {item.price_source && (
                                            <Typography variant="caption" color="text.secondary">
                                                Price: {item.price_source}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            type="number"
                                            value={Number(item.quantity || 0)}
                                            onChange={(e) => handleQuantityChange(index, parseFloat(e.target.value))}
                                            size="small"
                                            inputProps={{ min: 0.1, step: 0.1 }}
                                            sx={{ width: 100 }}
                                            disabled={!editMode}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            type="number"
                                            value={Number(item.unit_price || 0)}
                                            onChange={(e) => handlePriceChange(index, parseFloat(e.target.value))}
                                            size="small"
                                            inputProps={{ min: 0, step: 0.01 }}
                                            sx={{ width: 120 }}
                                            disabled={!editMode}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="medium">
                                            ₹{Number(item.line_total || 0).toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            value={item.notes || ''}
                                            onChange={(e) => handleNotesChange(index, e.target.value)}
                                            size="small"
                                            placeholder="Notes"
                                            sx={{ width: 150 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {editMode && (
                                            <IconButton
                                                onClick={() => handleRemoveItem(index)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add Item - only in edit mode */}
            {editMode && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <Autocomplete
                        options={availableItems}
                        getOptionLabel={(option) => option.name || ''}
                        value={selectedItem}
                        onChange={(_, value) => setSelectedItem(value)}
                        renderInput={(params) => (
                            <TextField {...params} label="Add Item" size="small" />
                        )}
                        sx={{ flex: 1, maxWidth: 400 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddItem}
                        disabled={!selectedItem}
                    >
                        Add
                    </Button>
                </Box>
            )}

            {/* Total */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Paper sx={{ p: 2, minWidth: 200 }}>
                    <Typography variant="h6" align="right">
                        Total: ₹{Number(total || 0).toFixed(2)}
                    </Typography>
                </Paper>
            </Box>
        </Box>
    );
};

export default SOItemsManager;
